/**
 * Progress persistence abstraction for resume playback.
 * Swap LocalProgressStorage for a backend adapter without changing the plugin.
 */

/**
 * @typedef {object} ProgressRecord
 * @property {number} position
 * @property {number} duration
 * @property {number} updatedAt
 * @property {boolean} [completed]
 */

export function createLocalProgressStorage(options) {
  options = options || {};
  var prefix = options.prefix || "ngp.progress.";

  function key(contentId) {
    return prefix + encodeURIComponent(String(contentId));
  }

  return {
    getName: function () {
      return "localStorage";
    },
    /**
     * @param {string} contentId
     * @returns {Promise<ProgressRecord|null>}
     */
    load: function (contentId) {
      return new Promise(function (resolve) {
        try {
          var raw = localStorage.getItem(key(contentId));
          resolve(raw ? JSON.parse(raw) : null);
        } catch (e) {
          resolve(null);
        }
      });
    },
    /**
     * @param {string} contentId
     * @param {ProgressRecord} record
     * @returns {Promise<void>}
     */
    save: function (contentId, record) {
      return new Promise(function (resolve) {
        try {
          localStorage.setItem(key(contentId), JSON.stringify(record));
        } catch (e) {
          /* quota / private mode */
        }
        resolve();
      });
    },
    /**
     * @param {string} contentId
     * @returns {Promise<void>}
     */
    clear: function (contentId) {
      return new Promise(function (resolve) {
        try {
          localStorage.removeItem(key(contentId));
        } catch (e) {
          /* ignore */
        }
        resolve();
      });
    },
  };
}

/**
 * Placeholder for authenticated cloud sync (Phase 4+).
 * @param {object} [options]
 */
export function createRemoteProgressStorage(options) {
  options = options || {};
  var inner = options.fallback || createLocalProgressStorage(options);
  return {
    getName: function () {
      return "remote-stub";
    },
    load: function (contentId) {
      // Future: GET /api/v1/playback-progress/:id — fall back locally for now
      return inner.load(contentId);
    },
    save: function (contentId, record) {
      // Future: PUT — always persist locally as cache
      return inner.save(contentId, record);
    },
    clear: function (contentId) {
      return inner.clear(contentId);
    },
  };
}

export default createLocalProgressStorage;
